// src/pages/BlogDetail.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../context/AuthContext';
import TuiEditor from '../components/TuiEditor';
import './BlogDetail.css';

function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const editorRef = useRef(null);
  const postTableRef = useRef(null);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const fetchPost = async () => {
    const yearList = ['2025', '2024', ''];
    let found = null;
    let foundTable = null;

    for (const year of yearList) {
      const tableName = year ? `blog_posts_${year}` : 'blog_posts';
      const { data } = await supabase.from(tableName).select('*').eq('slug', slug).single();
      if (data) {
        found = data;
        foundTable = tableName;
        break;
      }
    }

    if (!found) {
      setErrorMessage('해당 글을 찾을 수 없습니다.');
    } else {
      let cleanContent = found.content?.trim();
      if (cleanContent?.startsWith('<div class="contents_style">')) {
        cleanContent = cleanContent
          .replace(/^<div class="contents_style">/, '')
          .replace(/<\/div>\s*$/, '');
      }
      postTableRef.current = foundTable;
      setPost(found);
      setForm({
        title: found.title,
        summary: found.summary,
        content: cleanContent,
        thumbnail: found.thumbnail || '',
        tags: found.tags ? found.tags.join(', ') : '',
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    if (slug) fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isAdmin]);

  const canEdit = user?.email === 'calicorone@gmail.com' || user?.email === post?.author;

  const handleEdit = () => setIsEditing(true);

  const handleCancelEdit = () => {
    if (!post) return;
    setIsEditing(false);
    setForm({
      title: post.title,
      summary: post.summary,
      content: post.content,
      thumbnail: post.thumbnail,
      tags: post.tags?.join(', ') || '',
    });
  };

  const handleSaveEdit = async () => {
    const content = editorRef.current?.getMarkdown?.();
    const tableName = postTableRef.current;
    if (!slug || !tableName) {
      console.error('❌ 저장 실패: slug 또는 table이 없음');
      return;
    }

    const updatedData = {
      title: form.title,
      summary: form.summary,
      content,
      thumbnail: form.thumbnail?.trim() === '' ? null : form.thumbnail,
      tags: form.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    };

    const { error } = await supabase.from(tableName).update(updatedData).eq('slug', slug);

    if (error) {
      console.error('❌ Supabase 수정 실패:', error);
      setErrorMessage('글을 저장하는 중 오류가 발생했습니다.');
    } else {
      await fetchPost();
      setIsEditing(false);
    }
  };

  const handleDelete = async () => {
    const tableName = postTableRef.current;
    if (!tableName) return;
    if (window.confirm('정말 이 글을 삭제하시겠습니까?')) {
      const { error } = await supabase.from(tableName).delete().eq('slug', slug);
      if (error) {
        setErrorMessage('글을 삭제하는 중 오류가 발생했습니다.');
      } else {
        const yearPath = tableName.replace('blog_posts_', '') || '';
        navigate(yearPath ? `/blog/${yearPath}` : '/blog');
      }
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fileName = `thumbnail_${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from('thumbnails').upload(fileName, file);
    if (!error) {
      const { data } = supabase.storage.from('thumbnails').getPublicUrl(fileName);
      setForm((prev) => ({ ...prev, thumbnail: data.publicUrl }));
    }
  };

  const handleThumbnailDelete = () => {
    setForm((prev) => ({ ...prev, thumbnail: '' }));
  };

  const handleEditorChange = useCallback(() => {
    const content = editorRef.current?.getMarkdown?.();
    setForm((prev) => ({ ...prev, content }));
  }, []);

  if (loading) return <p>로딩 중...</p>;
  if (errorMessage) return <p>{errorMessage}</p>;
  if (!post || !form) return <p>해당 글을 찾을 수 없습니다.</p>;

  return (
    <div className="blog-detail-container">
      <h3 className="blog-detail-title">{post.title}</h3>
      <p className="blog-detail-date">{post.created_at?.replace('T', ' ').slice(0, 16)}</p>
      {post.summary && <p className="blog-detail-summary">{post.summary}</p>}
      <p className="blog-detail-author">
        작성자: {post.author === 'calicorone@gmail.com' ? '아현' : post.author}
      </p>
      {post.tags && post.tags.length > 0 && (
        <p className="blog-detail-tags">{post.tags.join(', ')}</p>
      )}

      {canEdit && !isEditing && (
        <div className="edit-delete-buttons">
          <button onClick={handleEdit} className="edit-btn">
            수정
          </button>
          <button onClick={handleDelete} className="delete-btn">
            삭제
          </button>
        </div>
      )}

      {isEditing ? (
        <div className="edit-form wide">
          <label htmlFor="title">제목</label>
          <input
            id="title"
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="input-field"
          />
          <label htmlFor="summary">요약</label>
          <input
            id="summary"
            type="text"
            value={form.summary}
            onChange={(e) => setForm({ ...form, summary: e.target.value })}
            className="input-field"
          />
          <label htmlFor="tags">태그</label>
          <input
            id="tags"
            type="text"
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            placeholder="#태그, 쉼표로 구분"
            className="input-field"
          />

          <TuiEditor
            ref={editorRef}
            height="600px"
            initialEditType="markdown"
            previewStyle="vertical"
            markdown={form.content}
            onChange={handleEditorChange}
          />

          <div className="thumbnail-upload">
            <label htmlFor="thumbnail">썸네일 수정</label>
            <input id="thumbnail" type="file" accept="image/*" onChange={handleThumbnailChange} />
            {form.thumbnail && (
              <div className="thumbnail-preview-container">
                <img src={form.thumbnail} alt="썸네일 미리보기" className="thumbnail-preview" />
                <button onClick={handleThumbnailDelete} className="remove-thumbnail-btn">
                  삭제
                </button>
              </div>
            )}
          </div>

          <div className="edit-buttons">
            <button onClick={handleSaveEdit} className="save-btn">
              저장
            </button>
            <button onClick={handleCancelEdit} className="cancel-btn">
              취소
            </button>
          </div>
        </div>
      ) : (
        <div className="blog-detail-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {form.content}
          </ReactMarkdown>
        </div>
      )}

      {(post.thumbnail || form.thumbnail) && (
        <div className="thumbnail-container">
          <img
            src={form.thumbnail || post.thumbnail}
            alt="썸네일"
            className="blog-thumbnail-detail"
          />
        </div>
      )}
    </div>
  );
}

export default BlogDetail;
